import { useId, useRef, useState } from 'react'
import { useSession } from '@clerk/clerk-react'
import { Questionnaire } from '@shadcn/react/questionnaire'
import {
  ArrowUp,
  Check,
  Circle,
  Loader2,
  Paperclip,
  Pencil,
} from 'lucide-react'
import { toast } from 'sonner'
import type { ClarifyingOption, ClarifyingQuestion } from '@/types'
import { uploadFile } from '@/features/files/api'
import { Button } from '@/components/ui/button'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import type { QuestionDraft } from './utils'

interface QuestionViewProps {
  question: ClarifyingQuestion
  draft: QuestionDraft
  disabled: boolean
  onChange: (next: QuestionDraft) => void
  // Called when the user picks a single-select option so the card can advance
  // after the selection has registered visually.
  onAdvance: () => void
  onSkip: () => void
  // False on the last question, where there is nothing to advance to and the
  // confirm arrow would do nothing that Submit does not already do.
  hasNextQuestion: boolean
}

function OptionBody({ option }: { option: ClarifyingOption }) {
  return (
    <>
      <Questionnaire.ChoiceShortcut className="flex size-5 shrink-0 items-center justify-center rounded bg-muted text-xs font-medium text-muted-foreground tabular-nums" />
      <span className="flex flex-col gap-0.5">
        <span className="flex items-center gap-2 text-sm font-medium text-foreground">
          {option.label}
          {option.recommended && (
            <span className="inline-flex items-center gap-1 rounded-full bg-david-solid/10 px-2 py-0.5 text-[11px] font-medium text-david-solid">
              <Check className="size-3" />
              Recommended
            </span>
          )}
        </span>
        {option.description && (
          // font-normal: the Label wrapper is font-bold; without this the
          // secondary line inherits bold.
          <span className="text-xs font-normal text-muted-foreground">
            {option.description}
          </span>
        )}
      </span>
    </>
  )
}

export function QuestionView({
  question,
  draft,
  disabled,
  onChange,
  onAdvance,
  onSkip,
  hasNextQuestion,
}: QuestionViewProps) {
  const multi = question.multi_select === true
  // With no options the text box is the only way to answer, so it stays even if
  // the model asked to suppress free text.
  const freeTextOnly = question.options.length === 0
  const showFreeText = freeTextOnly || question.allow_free_text !== false
  // A value the caller cannot proceed without: skipping it is a dead end, so
  // the card takes the answer or nothing.
  const required = question.required === true
  // Some answers are a file the user already has. Taking the upload here keeps
  // the whole job in the conversation.
  const wantsFile = question.allow_file === true
  const shortcutCount = Math.min(question.options.length, 9)

  const { session } = useSession()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const arrowSelectionRef = useRef(false)
  const titleId = useId()
  const [uploading, setUploading] = useState(false)

  const upload = async (file: File) => {
    const token = await session?.getToken()
    if (!token) {
      toast.error('Your session expired — refresh and try again.')
      return
    }
    setUploading(true)
    try {
      const { file: stored } = await uploadFile(
        token,
        file,
        question.file_purpose
      )
      if (!stored.url) {
        throw new Error(
          'the file was stored but could not be shared with the agent'
        )
      }
      // The URL is the answer: it is what a tool taking a file can be handed.
      onChange({ selected: [], freeText: stored.url, skipped: false })
      toast.success(`Uploaded ${stored.filename || file.name}`)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      toast.error(`Couldn't upload ${file.name}: ${message}`)
    } finally {
      setUploading(false)
    }
  }

  const toggle = (label: string) => {
    const selected = draft.selected.includes(label)
      ? draft.selected.filter((l) => l !== label)
      : [...draft.selected, label]
    onChange({ ...draft, selected, skipped: false })
  }

  const skip = () => {
    onChange({ selected: [], freeText: '', skipped: true })
    onSkip()
  }

  const rowClass = (selected: boolean) =>
    cn(
      'flex items-start gap-3 rounded-lg border p-3 transition-colors',
      // Opaque white options sit as the top layer over the card's frosted base.
      selected
        ? 'border-david-solid bg-david-solid/[0.06]'
        : 'border-border bg-background',
      // Hover previews the selected accent (a faint David tint) rather than a
      // muddy gray; scoped to unselected so it doesn't lighten a picked row.
      !disabled &&
        !selected &&
        'hover:border-david-solid/40 hover:bg-david-solid/[0.03]'
    )

  const options = question.options.map((option) => {
    const selected = draft.selected.includes(option.label)
    return (
      <Questionnaire.Choice
        key={option.label}
        checked={selected}
        disabled={disabled}
        value={option.label}
        className={cn(
          rowClass(selected),
          'group/choice cursor-pointer data-[disabled]:cursor-not-allowed'
        )}
        onPointerDown={() => {
          arrowSelectionRef.current = false
        }}
        onChange={(event) => {
          if (multi) {
            toggle(option.label)
            return
          }
          if (!event.target.checked) return
          onChange({
            ...draft,
            selected: [option.label],
            freeText: '',
            skipped: false,
          })
        }}>
        <Questionnaire.ChoiceInput
          className="peer sr-only"
          // Click, not change: re-picking the current option should still advance.
          onClick={() => {
            if (!multi && !arrowSelectionRef.current) onAdvance()
          }}
        />
        {multi ? (
          <span
            aria-hidden="true"
            className="mt-0.5 grid size-4 shrink-0 place-content-center rounded-sm border border-neutral-ui-border bg-neutral-app-bg transition-shadow peer-disabled:cursor-not-allowed peer-disabled:opacity-50 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-solid peer-focus-visible:ring-offset-2 group-data-[checked]/choice:border-primary group-data-[checked]/choice:bg-primary group-data-[checked]/choice:text-primary-foreground">
            <Check className="hidden size-3.5 group-data-[checked]/choice:block" />
          </span>
        ) : (
          <span
            aria-hidden="true"
            className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border border-input text-primary shadow-xs transition-[color,box-shadow] peer-disabled:cursor-not-allowed peer-disabled:opacity-50 peer-focus-visible:border-ring peer-focus-visible:ring-[3px] peer-focus-visible:ring-ring/50 dark:bg-input/30">
            <Circle className="hidden size-2 fill-primary group-data-[checked]/choice:block" />
          </span>
        )}
        <Questionnaire.ChoiceLabel className="flex flex-1 items-start gap-3">
          <OptionBody option={option} />
        </Questionnaire.ChoiceLabel>
      </Questionnaire.Choice>
    )
  })

  const hasFreeText = draft.freeText.trim().length > 0

  return (
    <Questionnaire.Item
      name={question.id}
      required={required}
      multiple={multi}
      aria-labelledby={titleId}
      onKeyDown={(event) => {
        const radioArrow =
          (event.key === 'ArrowLeft' || event.key === 'ArrowRight') &&
          event.target instanceof HTMLInputElement &&
          event.target.type === 'radio'
        arrowSelectionRef.current =
          event.key === 'ArrowUp' || event.key === 'ArrowDown' || radioArrow
      }}
      onKeyUp={(event) => {
        if (event.key.startsWith('Arrow')) arrowSelectionRef.current = false
      }}
      className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0 data-[active]:animate-in data-[active]:fade-in-0 data-[active]:duration-quick data-[active]:ease-smooth-out motion-reduce:animate-none">
      <Questionnaire.Title
        id={titleId}
        render={<p />}
        className="text-sm font-medium text-foreground">
        {question.question}
        {required && (
          <span className="ml-2 align-middle text-xs font-normal text-muted-foreground">
            Required
          </span>
        )}
      </Questionnaire.Title>

      {((multi && !freeTextOnly) || shortcutCount > 0) && (
        <Questionnaire.Description className="text-xs text-muted-foreground">
          {multi &&
            !freeTextOnly &&
            `Select all that apply, then choose ${hasNextQuestion ? 'Next' : 'Submit'}.`}
          {multi && !freeTextOnly && shortcutCount > 0 && ' '}
          {shortcutCount > 0 &&
            `Press ${shortcutCount === 1 ? '1' : `1–${shortcutCount}`} to choose.`}
        </Questionnaire.Description>
      )}

      {freeTextOnly ? null : (
        <Questionnaire.Choices className="flex flex-col gap-2">
          {options}
        </Questionnaire.Choices>
      )}

      {showFreeText ? (
        // Free-text row with an inline Skip (empty) / confirm arrow (once
        // typed) — mirrors the Cowork row. Alongside options it reads as
        // "Something else"; alone it is the answer box. Once typed it takes the
        // selected styling an option row gets, because typing is choosing.
        <InputGroup
          data-answer-chosen={hasFreeText || undefined}
          className={cn(
            'rounded-lg bg-background',
            hasFreeText && 'border-david-solid bg-david-solid/[0.06]'
          )}>
          <Label htmlFor={`${question.id}-other`} className="sr-only">
            {freeTextOnly
              ? question.question
              : `Other answer for ${question.question}`}
          </Label>
          <InputGroupAddon align="inline-start">
            {hasFreeText ? (
              <span
                aria-hidden="true"
                className="flex size-4 items-center justify-center rounded-full border-2 border-david-solid">
                <span className="size-1.5 rounded-full bg-david-solid" />
              </span>
            ) : (
              <Pencil className="size-4 text-muted-foreground" />
            )}
          </InputGroupAddon>
          <Questionnaire.Input
            render={<InputGroupInput />}
            id={`${question.id}-other`}
            value={draft.freeText}
            disabled={disabled}
            placeholder={
              question.placeholder ||
              (freeTextOnly ? 'Type your answer' : 'Something else')
            }
            onChange={(e) =>
              onChange({
                ...draft,
                freeText: e.target.value,
                // In single-select, typing a free-text answer replaces any
                // chosen option (they're mutually exclusive). Multi-select
                // keeps its checkboxes — free text is an extra item there.
                selected: multi ? draft.selected : [],
                skipped: false,
              })
            }
          />
          <InputGroupAddon align="inline-end">
            {wantsFile && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  aria-label="Upload a file"
                  className="hidden"
                  accept={question.file_types?.join(',') || undefined}
                  disabled={disabled || uploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    e.target.value = ''
                    if (file) void upload(file)
                  }}
                />
                <InputGroupButton
                  size="icon-xs"
                  variant="ghost"
                  disabled={disabled || uploading}
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="Choose a file to upload">
                  {uploading ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <Paperclip />
                  )}
                </InputGroupButton>
              </>
            )}
            {hasFreeText ? (
              hasNextQuestion ? (
                <Questionnaire.Next
                  disabled={disabled}
                  aria-label="Use this answer"
                  render={
                    <InputGroupButton
                      size="icon-xs"
                      variant="default"
                      className="rounded-full bg-david-solid text-white hover:bg-david-hover-solid">
                      <ArrowUp />
                    </InputGroupButton>
                  }
                />
              ) : null
            ) : required ? null : (
              <Questionnaire.Skip
                disabled={disabled}
                onClick={skip}
                render={<InputGroupButton variant="ghost" size="sm" />}
              />
            )}
          </InputGroupAddon>
        </InputGroup>
      ) : required ? null : (
        // No free text allowed — still offer Skip.
        <div className="flex justify-end">
          <Questionnaire.Skip
            disabled={disabled}
            onClick={skip}
            render={<Button variant="outline" size="sm" />}
          />
        </div>
      )}
      <Questionnaire.Error className="text-xs text-destructive" />
    </Questionnaire.Item>
  )
}
